import type { QueryClient } from "@tanstack/react-query";
import { voteTransactionKeys } from "./voteTransactionKeys";

export const VOTE_HISTORY_CHANGED_EVENT = "qit:vote-history-changed";
const pendingByClient = new WeakMap<QueryClient, Map<string, { timer: ReturnType<typeof setTimeout>; deadline: number }>>();

export const notifyVoteHistoryChanged = () => {
    if (typeof window !== "undefined") window.dispatchEvent(new Event(VOTE_HISTORY_CHANGED_EVENT));
};

// Mark history stale immediately, then refresh active views after a quiet
// second. Sustained voting still refreshes at least once every three seconds.
export const scheduleVoteTransactionRefresh = (client: QueryClient, userId: string) => {
    if (!userId) return;
    const pending = pendingByClient.get(client) ?? new Map<string, { timer: ReturnType<typeof setTimeout>; deadline: number }>();
    pendingByClient.set(client, pending);
    const existing = pending.get(userId);
    const deadline = existing?.deadline ?? Date.now() + 3_000;
    if (existing) clearTimeout(existing.timer);
    const queryKey = voteTransactionKeys.user(userId);
    void client.invalidateQueries({ queryKey, refetchType: "none" });
    // With no active history view, marking stale is sufficient. Its next mount
    // fetches once; a delayed refetch would only compete with that request.
    if (!client.getQueryCache().find({ queryKey, exact: true })?.isActive()) {
        pending.delete(userId);
        return;
    }
    const timer = setTimeout(() => {
        pending.delete(userId);
        void client.invalidateQueries({ queryKey });
    }, Math.min(1_000, Math.max(0, deadline - Date.now())));
    pending.set(userId, { timer, deadline });
};
